import React from 'react';
import Input from '@material-ui/core/OutlinedInput';
import Arrow from '../../../icons/Arrow';
import IconButton from '../IconButton/IconButton';
import useStyles from './NumericInput.styles';

export interface NumericInputProps {
  onChange: (val: number) => void;
  dataQaId: string;
  value: number;
  disabled?: boolean;
  minValue?: number;
  maxValue?: number;
  step?: number;
  errorMsg?: string;
  helperText?: string;
  label?: string;
  displayFormatter?: (val: number) => number | string;
}

const NumericInput = ({
  onChange,
  disabled,
  dataQaId,
  value,
  step = 1,
  minValue,
  maxValue,
  errorMsg,
  helperText,
  label,
  displayFormatter = (val: number) => val,
}: NumericInputProps): React.ReactElement => {
  const classes = useStyles({ errorMsg: errorMsg || '' });

  const onValueIncrease = (): void => onValueChange(step);
  const onValueDecrease = (): void => onValueChange(-step);

  const onValueChange = (diff: number): void => onChange(value + diff);

  const isIncreaseValueDisabled = maxValue !== undefined && maxValue !== null ? value + step > maxValue : false;
  const isDecreaseValueDisabled = minValue !== undefined && minValue !== null ? value - step < minValue : false;

  const HelperMessage = () => (
    <span className={classes.helperText} data-qa-id={`${dataQaId}-${errorMsg ? 'error' : 'helper'}`}>
      {errorMsg || helperText}
    </span>
  );

  return (
    <>
      {label && (
        <label htmlFor={dataQaId} className={classes.label} data-qa-id={`${dataQaId}-label`}>
          {label}
        </label>
      )}
      <Input
        fullWidth
        id={dataQaId}
        readOnly={true}
        value={value}
        disabled={disabled}
        error={!!errorMsg}
        classes={{
          root: classes.input,
        }}
        endAdornment={
          <div className={classes.endAdornment}>
            <IconButton
              onClick={onValueIncrease}
              disabled={disabled || isIncreaseValueDisabled}
              qaId={`${dataQaId}-button-increase`}
            >
              <Arrow type="up" disabled={disabled || isIncreaseValueDisabled} />
            </IconButton>
            <IconButton
              onClick={onValueDecrease}
              disabled={disabled || isDecreaseValueDisabled}
              qaId={`${dataQaId}-button-decrease`}
            >
              <Arrow type="down" disabled={disabled || isDecreaseValueDisabled} />
            </IconButton>
          </div>
        }
        inputProps={{
          'data-qa-id': dataQaId,
          'aria-label': 'numeric-input',
          value: displayFormatter(value),
        }}
      />
      {(helperText || errorMsg) && <HelperMessage />}
    </>
  );
};

export default NumericInput;
